'use client';

import { motion, useScroll, useMotionValueEvent } from 'framer-motion';
import React, { useState, useEffect, Suspense } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from "next/navigation";
import AutocompleteSearch from '@/components/partials/search/AutocompleteSearch';

import { Config } from '@/types/config.type';

import brandNameImage from '../../public/images/logo/nrdrealestate__logo--white.svg';

import {
    ChartBarIcon,
    HomeModernIcon,
    WrenchScrewdriverIcon,
    LightBulbIcon,
    Bars2Icon,
    ArrowRightIcon
} from '@heroicons/react/24/solid';
import Button from "./partials/button";

const iconMap: Record<string, React.ReactNode> = {
    ChartBarIcon: <ChartBarIcon className="w-6 h-6 text-white bg-gray-900 p-1.5 rounded-lg" />,
    HomeModernIcon: <HomeModernIcon className="w-6 h-6 text-white bg-gray-900 p-1.5 rounded-lg" />,
    WrenchScrewdriverIcon: <WrenchScrewdriverIcon className="w-6 h-6 text-white bg-gray-900 p-1.5 rounded-lg" />,
    LightBulbIcon: <LightBulbIcon className="w-6 h-6 text-white bg-gray-900 p-1.5 rounded-lg" />
};

type NavbarSubItem = {
    title: string;
    description: string;
    icon: string;
    url: string;
};

type NavbarMenuItem = {
    name: string;
    url: string;
    subMenu?: NavbarSubItem[];
};

type HeaderProps = {
    config: Config;
    data: {
        navbarMenu: NavbarMenuItem[];
    };
};

const Header = ({ config, data }: HeaderProps) => {
    const pathname = usePathname();
    const isPartnersPage = pathname === "/partners";

    const { scrollY } = useScroll();
    const [hidden, setHidden] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [activeMenu, setActiveMenu] = useState<string | null>(null);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 1024);
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useMotionValueEvent(scrollY, 'change', (latest) => {
        const previous = scrollY.getPrevious() ?? 0;
        if (latest > previous && latest > 150) setHidden(true);
        else setHidden(false);
    });

    const { company } = config;
    const { navbarMenu } = data;

    const isInternal = (url?: string) => !!url && url.startsWith('/');
    const isActive = (url?: string) => {
        if (!url || !url.startsWith('/')) return false;
        if (url === '/') return pathname === '/';
        return pathname === url || pathname.startsWith(url + '/') || pathname.startsWith(url);
    };
    const baseLinkClass = "font-black sm:p-4 whitespace-nowrap";

    return (
        <motion.header
            variants={{ hidden: { y: 0 }, visible: { y: '-100%' } }}
            animate={menuOpen ? 'hidden' : (hidden ? 'visible' : 'hidden')}
            initial={false}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            style={{
                position: isMobile ? 'static' : 'sticky',
                top: 0,
                zIndex: 50,
                width: '100%',
                backgroundColor: 'var(--background-color)'
            }}
        >
            <nav className="w-full px-4 py-2 sm:px-6 sm:py-4" role="navigation" aria-label="Primary">
                <div className="flex w-full items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                        <a href={company.website} className="flex items-center space-x-3 rtl:space-x-reverse shrink-0 flex-none" title={company.name} aria-label={company.name}>
                            <span className="self-center text-2xl font-black">
                                <Image
                                    className="h-[2.375rem] w-[6.25rem] shrink-0"
                                    width={100}
                                    height={38}
                                    sizes="100px"
                                    src={brandNameImage}
                                    alt={company.name}
                                    priority
                                />
                            </span>
                        </a>

                        <div className="hidden lg:flex flex-1 min-w-0 max-w-lg">
                            <Suspense fallback={<div className="w-full h-9" />}>
                                <AutocompleteSearch />
                            </Suspense>
                        </div>

                        <div className="hidden lg:flex space-x-4 flex-wrap items-center min-w-0" id="navbar-cta">
                            {navbarMenu.map((menuItem, index) => (
                                <div
                                    key={index}
                                    className="relative group"
                                    onMouseEnter={() => setActiveMenu(menuItem.name)}
                                    onMouseLeave={() => setActiveMenu(null)}
                                >
                                    {isInternal(menuItem.url) ? (
                                        <Link
                                            href={menuItem.url}
                                            className={`${baseLinkClass} ${isActive(menuItem.url) ? 'text-purple-400' : ''}`}
                                            aria-current={isActive(menuItem.url) ? 'page' : undefined}
                                            aria-haspopup={menuItem.subMenu ? true : undefined}
                                            aria-expanded={menuItem.subMenu ? (activeMenu === menuItem.name) : undefined}
                                        >
                                            {menuItem.name}
                                        </Link>
                                    ) : (
                                        <a
                                            href={menuItem.url}
                                            className={`${baseLinkClass} ${isActive(menuItem.url) ? 'text-purple-400' : ''}`}
                                            aria-current={isActive(menuItem.url) ? 'page' : undefined}
                                            aria-haspopup={menuItem.subMenu ? true : undefined}
                                            aria-expanded={menuItem.subMenu ? (activeMenu === menuItem.name) : undefined}
                                        >
                                            {menuItem.name}
                                        </a>
                                    )}

                                    {menuItem.subMenu && activeMenu === menuItem.name && (
                                        <div className="absolute left-0 top-full mt-3 w-[600px] p-6 bg-[var(--background2-color)] rounded-lg shadow-black shadow-2xl border-2 border-[var(--border-color)] grid grid-cols-2 gap-4 z-50">
                                            {menuItem.subMenu.map((sub, subIndex) => (
                                                isInternal(sub.url) ? (
                                                    <Link key={subIndex} href={sub.url} className="flex items-start hover:bg-neutral-900 rounded-lg p-2 px-4" aria-label={sub.title}>
                                                        <span className="mt-2 me-3">
                                                            <span className="inline-flex shrink-0 rounded-full border border-purple-400 bg-[var(--border-color)] p-2" aria-hidden="true">
                                                                <svg className="size-6">
                                                                    {iconMap[sub.icon]}
                                                                </svg>
                                                            </span>
                                                        </span>
                                                        <div className="flex flex-col">
                                                            <h4 className="text-base text-white font-semibold leading-7">{sub.title}</h4>
                                                            <h6 className="text-sm text-[var(--body-1-color)]">{sub.description}</h6>
                                                        </div>
                                                    </Link>
                                                ) : (
                                                    <a key={subIndex} href={sub.url} className="flex items-start hover:bg-neutral-900 rounded-lg p-2 px-4" aria-label={sub.title}>
                                                        <span className="mt-2 me-3">
                                                            <span className="inline-flex shrink-0 rounded-full border border-purple-400 bg-[var(--border-color)] p-2" aria-hidden="true">
                                                                <svg className="size-6">
                                                                    {iconMap[sub.icon]}
                                                                </svg>
                                                            </span>
                                                        </span>
                                                        <div className="flex flex-col">
                                                            <h4 className="text-base text-white font-semibold leading-7">{sub.title}</h4>
                                                            <h6 className="text-sm text-[var(--body-1-color)]">{sub.description}</h6>
                                                        </div>
                                                    </a>
                                                )
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="flex lg:hidden">
                            <button
                                onClick={() => setMenuOpen(!menuOpen)}
                                className=""
                                aria-label="Toggle navigation"
                                aria-expanded={menuOpen}
                            >
                                <Bars2Icon className="h-8 w-8 text-white" aria-hidden="true" />
                            </button>
                        </div>
                        <div className="hidden lg:flex">
                            {isPartnersPage ? (
                                <Button
                                    id={`${Header.name}-partner`}
                                    href="https://nrdrealestate.com/scheduler"
                                    type="outlined"
                                    target="_blank"
                                    className="flex w-full md:text-sm"
                                    ariaLabel={`${Header.name} schedule appointment`}
                                    icon={
                                        <ArrowRightIcon className="flex-shrink-0 w-5 h-5 ms-2" />
                                    }
                                    iconPosition="right"
                                >
                                    Schedule Appointment
                                </Button>
                            ) : (
                                <Button
                                    id={`${Header.name}-partner`}
                                    href="/partners"
                                    type="outlined"
                                    className="flex w-full md:text-sm"
                                    ariaLabel={`${Header.name} partner`}
                                    icon={
                                        <ArrowRightIcon className="flex-shrink-0 w-5 h-5 ms-2" />
                                    }
                                    iconPosition="right"
                                >
                                    Build Generational Wealth
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
                <div className={`w-full ${menuOpen ? 'block' : 'hidden'} lg:hidden space-y-4 mt-4`} id="navbar-mobile">
                    {navbarMenu.map((menuItem, index) => (
                        <div
                            key={index}
                            className="relative group"
                            onMouseEnter={() => setActiveMenu(menuItem.name)}
                            onMouseLeave={() => setActiveMenu(null)}
                        >
                            {menuItem.url ? (
                                isInternal(menuItem.url) ? (
                                    <Link
                                        href={menuItem.url}
                                        className={`font-black block p-4 whitespace-nowrap ${isActive(menuItem.url) ? 'text-purple-400' : ''}`}
                                        aria-current={isActive(menuItem.url) ? 'page' : undefined}
                                        aria-haspopup={menuItem.subMenu ? true : undefined}
                                        aria-expanded={menuItem.subMenu ? (activeMenu === menuItem.name) : undefined}
                                    >
                                        {menuItem.name}
                                    </Link>
                                ) : (
                                    <a
                                        href={menuItem.url}
                                        className={`font-black block p-4 whitespace-nowrap ${isActive(menuItem.url) ? 'text-purple-400' : ''}`}
                                        aria-current={isActive(menuItem.url) ? 'page' : undefined}
                                        aria-haspopup={menuItem.subMenu ? true : undefined}
                                        aria-expanded={menuItem.subMenu ? (activeMenu === menuItem.name) : undefined}
                                    >
                                        {menuItem.name}
                                    </a>
                                )
                            ) : (
                                <button
                                    type="button"
                                    className="font-black block p-4 whitespace-nowrap"
                                    aria-haspopup="true"
                                    aria-expanded={activeMenu === menuItem.name}
                                >
                                    {menuItem.name}
                                </button>
                            )}

                            {menuItem.subMenu && (
                                <div className="w-full bg-[var(--background2-color)] rounded-lg shadow-black shadow-2xl border-2 border-[var(--border-color)] p-6 grid grid-cols-1 gap-4 z-50">
                                    {menuItem.subMenu.map((sub, subIndex) => (
                                        isInternal(sub.url) ? (
                                            <Link key={subIndex} href={sub.url} className="flex items-start hover:bg-neutral-900 rounded-lg p-2 px-4" aria-label={sub.title}>
                                                <span className="mt-1  me-3">
                                                    <span className="inline-flex shrink-0 rounded-full border border-purple-400 bg-[var(--border-color)] p-2" aria-hidden="true">
                                                        <svg className="size-6">
                                                            {iconMap[sub.icon]}
                                                        </svg>
                                                    </span>
                                                </span>
                                                <div className="flex flex-col">
                                                    <h4 className="text-base text-white font-semibold leading-7">{sub.title}</h4>
                                                    <h6 className="text-sm text-[var(--body-1-color)]">{sub.description}</h6>
                                                </div>
                                            </Link>
                                        ) : (
                                            <a key={subIndex} href={sub.url} className="flex items-start hover:bg-neutral-900 rounded-lg p-2 px-4" aria-label={sub.title}>
                                                <span className="mt-1  me-3">
                                                    <span className="inline-flex shrink-0 rounded-full border border-purple-400 bg-[var(--border-color)] p-2" aria-hidden="true">
                                                        <svg className="size-6">
                                                            {iconMap[sub.icon]}
                                                        </svg>
                                                    </span>
                                                </span>
                                                <div className="flex flex-col">
                                                    <h4 className="text-base text-white font-semibold leading-7">{sub.title}</h4>
                                                    <h6 className="text-sm text-[var(--body-1-color)]">{sub.description}</h6>
                                                </div>
                                            </a>
                                        )
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                    <div className="block md:block lg:hidden">

                        {isPartnersPage ? (
                            <Button
                                id={`${Header.name}-partner`}
                                href="https://nrdrealestate.com/scheduler"
                                type="outlined"
                                target="_blank"
                                className="flex w-full md:text-sm"
                                ariaLabel={`${Header.name} schedule appointment`}
                                icon={
                                    <ArrowRightIcon className="flex-shrink-0 w-5 h-5 ms-2" />
                                }
                                iconPosition="right"
                            >
                                Schedule Appointment
                            </Button>
                        ) : (
                            <Button
                                id={`${Header.name}-partner`}
                                href="/partners"
                                type="outlined"
                                className="flex w-full md:text-sm"
                                ariaLabel={`${Header.name} partner`}
                                icon={
                                    <ArrowRightIcon className="flex-shrink-0 w-5 h-5 ms-2" />
                                }
                                iconPosition="right"
                            >
                                Build Generational Wealth
                            </Button>
                        )}
                    </div>
                </div>
            </nav>
        </motion.header >
    );
};

export default Header;